import React, { useState } from 'react';
import {
  FileCheck, Search, Filter, Download, CheckCircle, XCircle,
  AlertCircle, RefreshCw, Link as LinkIcon, Calendar, Building2, X, Loader2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const ReconciliationTab = () => {
  const { language, t } = useLanguage();
  const [activeSubTab, setActiveSubTab] = useState('bank-reconciliation');
  const [showReconcileModal, setShowReconcileModal] = useState(false);
  const [isReconciling, setIsReconciling] = useState(false);
  const [reconcileResults, setReconcileResults] = useState(null);

  const subTabs = [
    { id: 'bank-reconciliation', label: t('bankReconciliation') },
    { id: 'inventory-reconciliation', label: t('inventoryReconciliation') },
    { id: 'ap-reconciliation', label: t('apReconciliation') },
    { id: 'ar-reconciliation', label: t('arReconciliation') }
  ];

  const bankReconciliations = [
    {
      id: 'BR-2024-001',
      account: 'Main Operating Account',
      accountNumber: '****1234',
      bank: 'Al Rajhi Bank',
      statementDate: '2024-02-10',
      statementBalance: 3890000,
      bookBalance: 3895000,
      difference: 5000,
      status: 'matched',
      lastReconciled: '2024-02-10',
      outstandingItems: 0
    },
    {
      id: 'BR-2024-002',
      account: 'Payroll Account',
      accountNumber: '****5678',
      bank: 'SABB',
      statementDate: '2024-02-10',
      statementBalance: 1250000,
      bookBalance: 1250000,
      difference: 0,
      status: 'matched',
      lastReconciled: '2024-02-10',
      outstandingItems: 0
    },
    {
      id: 'BR-2024-003',
      account: 'Savings Account',
      accountNumber: '****9012',
      bank: 'NCB',
      statementDate: '2024-02-08',
      statementBalance: 2500000,
      bookBalance: 2505000,
      difference: 5000,
      status: 'pending',
      lastReconciled: '2024-01-31',
      outstandingItems: 3
    }
  ];

  const inventoryReconciliation = [
    {
      id: 'INV-REC-001',
      module: 'WMS',
      glAccount: '1130 - Inventory',
      wmsQuantity: 12500,
      glValue: 2865000,
      wmsValue: 2865000,
      difference: 0,
      status: 'matched',
      lastReconciled: '2024-02-11',
      variance: 0
    },
    {
      id: 'INV-REC-002',
      module: 'Warehouse',
      glAccount: '1130 - Inventory',
      wmsQuantity: 8500,
      glValue: 1950000,
      wmsValue: 1950000,
      difference: 0,
      status: 'matched',
      lastReconciled: '2024-02-11',
      variance: 0
    },
    {
      id: 'INV-REC-003',
      module: 'Retail Stores',
      glAccount: '1130 - Inventory',
      wmsQuantity: 3200,
      glValue: 735000,
      wmsValue: 740000,
      difference: 5000,
      status: 'pending',
      lastReconciled: '2024-02-09',
      variance: 5000
    }
  ];

  const apReconciliation = [
    {
      id: 'AP-REC-001',
      vendor: 'ABC Supplies Co.',
      glBalance: 450000,
      subledgerBalance: 450000,
      difference: 0,
      status: 'matched',
      lastReconciled: '2024-02-11',
      unmatchedInvoices: 0
    },
    {
      id: 'AP-REC-002',
      vendor: 'XYZ Manufacturing',
      glBalance: 320000,
      subledgerBalance: 320000,
      difference: 0,
      status: 'matched',
      lastReconciled: '2024-02-11',
      unmatchedInvoices: 0
    },
    {
      id: 'AP-REC-003',
      vendor: 'Global Logistics',
      glBalance: 189000,
      subledgerBalance: 194000,
      difference: 5000,
      status: 'pending',
      lastReconciled: '2024-02-09',
      unmatchedInvoices: 1
    }
  ];

  const arReconciliation = [
    {
      id: 'AR-REC-001',
      customer: 'Retail Store Chain A',
      glBalance: 650000,
      subledgerBalance: 650000,
      difference: 0,
      status: 'matched',
      lastReconciled: '2024-02-11',
      unmatchedInvoices: 0
    },
    {
      id: 'AR-REC-002',
      customer: 'Wholesale Distributor B',
      glBalance: 420000,
      subledgerBalance: 420000,
      difference: 0,
      status: 'matched',
      lastReconciled: '2024-02-11',
      unmatchedInvoices: 0
    },
    {
      id: 'AR-REC-003',
      customer: 'Corporate Client C',
      glBalance: 285000,
      subledgerBalance: 290000,
      difference: 5000,
      status: 'pending',
      lastReconciled: '2024-02-09',
      unmatchedInvoices: 1
    }
  ];

  const getStatusBadge = (status) => {
    if (status === 'matched') {
      return <span className={`px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-700 flex items-center gap-1 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
        <CheckCircle className="h-3 w-3" />
        {t('matched')}
      </span>;
    }
    return <span className={`px-2 py-1 text-xs font-medium rounded bg-yellow-100 text-yellow-700 flex items-center gap-1 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
      <AlertCircle className="h-3 w-3" />
      {t('pending')}
    </span>;
  };

  return (
    <div className={`space-y-6 ${language === 'ar' ? 'rtl' : 'ltr'}`}>
      {/* Sub-tabs */}
      <div className={`flex items-center gap-2 border-b border-gray-200 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
        {subTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeSubTab === tab.id
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Actions */}
      <div className={`flex items-center justify-between ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
        <div className={`flex items-center gap-3 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
          <button 
            onClick={() => alert(t('filterFunctionality'))}
            className={`px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 flex items-center gap-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}
          >
            <Filter className="h-4 w-4" />
            {t('filter')}
          </button>
          <button 
            onClick={() => alert('Date Range picker would open here')}
            className={`px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 flex items-center gap-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}
          >
            <Calendar className="h-4 w-4" />
            {language === 'ar' ? 'نطاق التاريخ' : 'Date Range'}
          </button>
        </div>
        <div className={`flex items-center gap-3 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
          <button 
            onClick={() => alert(t('exportFunctionality'))}
            className={`px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 flex items-center gap-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}
          >
            <Download className="h-4 w-4" />
            {t('export')}
          </button>
          <button 
            onClick={() => {
              setShowReconcileModal(true);
              setIsReconciling(true);
              setReconcileResults(null);
              
              // Simulate reconciliation process
              setTimeout(() => {
                setIsReconciling(false);
                setReconcileResults({
                  success: true,
                  totalAccounts: 3,
                  reconciledAccounts: 2,
                  pendingAccounts: 1,
                  matchedItems: 45,
                  unmatchedItems: 3,
                  totalDifference: 10000,
                  message: 'Reconciliation completed successfully!',
                  details: [
                    { account: 'Main Operating Account', status: 'Matched', difference: 0 },
                    { account: 'Payroll Account', status: 'Matched', difference: 0 },
                    { account: 'Savings Account', status: 'Pending', difference: 10000, outstandingItems: 3 }
                  ]
                });
              }, 2000);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            {t('reconcileNow')}
          </button>
        </div>
      </div>

      {/* Bank Reconciliation */}
      {activeSubTab === 'bank-reconciliation' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className={`p-5 border-b border-gray-200 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
            <h3 className="text-lg font-semibold text-gray-900">{t('bankAccountsReconciliation')}</h3>
          </div>
          <div className="overflow-x-auto">
            <table className={`w-full text-sm ${language === 'ar' ? 'text-right' : 'text-left'}`}>
              <thead>
                <tr className="text-xs font-semibold text-gray-600 uppercase border-b border-gray-200 bg-gray-50">
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('account')}</th>
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('bank')}</th>
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('statementDate')}</th>
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('statementBalance')}</th>
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('bookBalance')}</th>
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('difference')}</th>
                  <th className={`py-3 px-4 text-center`}>{t('outstandingItems')}</th>
                  <th className={`py-3 px-4 text-center`}>{t('status')}</th>
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('lastReconciled')}</th>
                  <th className={`py-3 px-4 text-center`}>{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {bankReconciliations.map((recon) => (
                  <tr key={recon.id} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                      <div className={language === 'ar' ? 'text-right' : 'text-left'}>
                        <p className="font-medium text-gray-900">{recon.account}</p>
                        <p className="text-xs text-gray-500">{recon.accountNumber}</p>
                      </div>
                    </td>
                    <td className={`py-3 px-4 text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{recon.bank}</td>
                    <td className={`py-3 px-4 text-gray-600 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{recon.statementDate}</td>
                    <td className={`py-3 px-4 font-medium text-gray-900 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                      SAR {recon.statementBalance.toLocaleString()}
                    </td>
                    <td className={`py-3 px-4 font-medium text-gray-900 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                      SAR {recon.bookBalance.toLocaleString()}
                    </td>
                    <td className={`py-3 px-4 font-semibold ${
                      recon.difference === 0 ? 'text-green-600' : 'text-red-600'
                    } ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                      SAR {recon.difference.toLocaleString()}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        recon.outstandingItems === 0 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {recon.outstandingItems}
                      </span>
                    </td>
                    <td className="py-3 px-4">{getStatusBadge(recon.status)}</td>
                    <td className={`py-3 px-4 text-gray-600 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{recon.lastReconciled}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-center gap-2">
                        <button 
                          onClick={() => alert(`${language === 'ar' ? 'جاري التوفيق بين' : 'Reconciling'} ${recon.account}... ${language === 'ar' ? 'سيتم فتح عرض تفاصيل التوفيق.' : 'This would open reconciliation detail view.'}`)}
                          className="p-1.5 hover:bg-gray-200 rounded"
                          title={t('reconcile')}
                        >
                          <FileCheck className="h-4 w-4 text-gray-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Inventory Reconciliation */}
      {activeSubTab === 'inventory-reconciliation' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="p-5 border-b border-gray-200">
            <h3 className={`text-lg font-semibold text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('inventoryModuleReconciliation')}</h3>
          </div>
          <div className="overflow-x-auto">
            <table className={`w-full text-sm ${language === 'ar' ? 'text-right' : 'text-left'}`}>
              <thead>
                <tr className="text-xs font-semibold text-gray-600 uppercase border-b border-gray-200 bg-gray-50">
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('module')}</th>
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('glAccount')}</th>
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('wmsQuantity')}</th>
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('glValue')}</th>
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('wmsValue')}</th>
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('variance')}</th>
                  <th className={`py-3 px-4 text-center`}>{t('status')}</th>
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('lastReconciled')}</th>
                  <th className={`py-3 px-4 text-center`}>{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {inventoryReconciliation.map((recon) => (
                  <tr key={recon.id} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className={`py-3 px-4 font-medium text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{recon.module}</td>
                    <td className={`py-3 px-4 text-gray-600 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{recon.glAccount}</td>
                    <td className={`py-3 px-4 text-gray-900 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{recon.wmsQuantity.toLocaleString()}</td>
                    <td className={`py-3 px-4 font-medium text-gray-900 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                      SAR {recon.glValue.toLocaleString()}
                    </td>
                    <td className={`py-3 px-4 font-medium text-gray-900 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                      SAR {recon.wmsValue.toLocaleString()}
                    </td>
                    <td className={`py-3 px-4 font-semibold ${
                      recon.variance === 0 ? 'text-green-600' : 'text-red-600'
                    } ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                      SAR {recon.variance.toLocaleString()}
                    </td>
                    <td className="py-3 px-4">{getStatusBadge(recon.status)}</td>
                    <td className={`py-3 px-4 text-gray-600 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{recon.lastReconciled}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-center gap-2">
                        <button 
                          onClick={() => alert(t('linkingReconciliation'))}
                          className="p-1.5 hover:bg-gray-200 rounded"
                          title={t('linkItems')}
                        >
                          <LinkIcon className="h-4 w-4 text-gray-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* AP Reconciliation */}
      {activeSubTab === 'ap-reconciliation' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="p-5 border-b border-gray-200">
            <h3 className={`text-lg font-semibold text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('accountsPayableReconciliation')}</h3>
          </div>
          <div className="overflow-x-auto">
            <table className={`w-full text-sm ${language === 'ar' ? 'text-right' : 'text-left'}`}>
              <thead>
                <tr className="text-xs font-semibold text-gray-600 uppercase border-b border-gray-200 bg-gray-50">
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('vendor')}</th>
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('glBalance')}</th>
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('subledgerBalance')}</th>
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('difference')}</th>
                  <th className={`py-3 px-4 text-center`}>{t('unmatchedInvoices')}</th>
                  <th className={`py-3 px-4 text-center`}>{t('status')}</th>
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('lastReconciled')}</th>
                  <th className={`py-3 px-4 text-center`}>{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {apReconciliation.map((recon) => (
                  <tr key={recon.id} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className={`py-3 px-4 font-medium text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{recon.vendor}</td>
                    <td className={`py-3 px-4 font-medium text-gray-900 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                      SAR {recon.glBalance.toLocaleString()}
                    </td>
                    <td className={`py-3 px-4 font-medium text-gray-900 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                      SAR {recon.subledgerBalance.toLocaleString()}
                    </td>
                    <td className={`py-3 px-4 font-semibold ${
                      recon.difference === 0 ? 'text-green-600' : 'text-red-600'
                    } ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                      SAR {recon.difference.toLocaleString()}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        recon.unmatchedInvoices === 0 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {recon.unmatchedInvoices}
                      </span>
                    </td>
                    <td className="py-3 px-4">{getStatusBadge(recon.status)}</td>
                    <td className={`py-3 px-4 text-gray-600 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{recon.lastReconciled}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-center gap-2">
                        <button 
                          onClick={() => alert(t('linkingReconciliation'))}
                          className="p-1.5 hover:bg-gray-200 rounded"
                          title={t('linkItems')}
                        >
                          <LinkIcon className="h-4 w-4 text-gray-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* AR Reconciliation */}
      {activeSubTab === 'ar-reconciliation' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="p-5 border-b border-gray-200">
            <h3 className={`text-lg font-semibold text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('accountsReceivableReconciliation')}</h3>
          </div>
          <div className="overflow-x-auto">
            <table className={`w-full text-sm ${language === 'ar' ? 'text-right' : 'text-left'}`}>
              <thead>
                <tr className="text-xs font-semibold text-gray-600 uppercase border-b border-gray-200 bg-gray-50">
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('customer')}</th>
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('glBalance')}</th>
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('subledgerBalance')}</th>
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('difference')}</th>
                  <th className={`py-3 px-4 text-center`}>{t('unmatchedInvoices')}</th>
                  <th className={`py-3 px-4 text-center`}>{t('status')}</th>
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('lastReconciled')}</th>
                  <th className={`py-3 px-4 text-center`}>{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {arReconciliation.map((recon) => (
                  <tr key={recon.id} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className={`py-3 px-4 font-medium text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{recon.customer}</td>
                    <td className={`py-3 px-4 font-medium text-gray-900 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                      SAR {recon.glBalance.toLocaleString()}
                    </td>
                    <td className={`py-3 px-4 font-medium text-gray-900 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                      SAR {recon.subledgerBalance.toLocaleString()}
                    </td>
                    <td className={`py-3 px-4 font-semibold ${
                      recon.difference === 0 ? 'text-green-600' : 'text-red-600'
                    } ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                      SAR {recon.difference.toLocaleString()}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        recon.unmatchedInvoices === 0 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {recon.unmatchedInvoices}
                      </span>
                    </td>
                    <td className="py-3 px-4">{getStatusBadge(recon.status)}</td>
                    <td className={`py-3 px-4 text-gray-600 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{recon.lastReconciled}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-center gap-2">
                        <button 
                          onClick={() => alert(t('linkingReconciliation'))}
                          className="p-1.5 hover:bg-gray-200 rounded"
                          title={t('linkItems')}
                        >
                          <LinkIcon className="h-4 w-4 text-gray-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Reconciliation Modal */}
      <AnimatePresence>
        {showReconcileModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => {
              if (!isReconciling) {
                setShowReconcileModal(false);
                setReconcileResults(null);
              }
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            >
              {/* Header */}
              <div className={`p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                <div className={`flex items-center justify-between ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{t('reconciliationProcess')}</h2>
                    <p className="text-sm text-gray-600 mt-1">
                      {isReconciling ? t('processingReconciliation') : t('reconciliationCompleted')}
                    </p>
                  </div>
                  {!isReconciling && (
                    <button
                      onClick={() => {
                        setShowReconcileModal(false);
                        setReconcileResults(null);
                      }}
                      className="p-2 hover:bg-white rounded-lg transition-colors"
                    >
                      <X className="h-5 w-5 text-gray-600" />
                    </button>
                  )}
                </div>
              </div>

              {/* Content */}
              <div className={`flex-1 overflow-y-auto p-6 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                {isReconciling ? (
                  <div className={`flex flex-col items-center justify-center py-12 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                    <Loader2 className="h-12 w-12 text-blue-600 animate-spin mb-4" />
                    <p className="text-lg font-medium text-gray-900 mb-2">{t('reconcilingAccounts')}</p>
                    <p className={`text-sm text-gray-600 max-w-md ${language === 'ar' ? 'text-right' : 'text-center'}`}>
                      {t('comparingStatements')}
                    </p>
                    <div className={`mt-6 w-full max-w-md ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                      <div className={`flex items-center justify-between text-sm text-gray-600 mb-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                        <span>{t('processingBankAccounts')}</span>
                        <span>✓</span>
                      </div>
                      <div className={`flex items-center justify-between text-sm text-gray-600 mb-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                        <span>{t('processingInventoryRecords')}</span>
                        <span>✓</span>
                      </div>
                      <div className={`flex items-center justify-between text-sm text-gray-600 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                        <span>{t('matchingTransactions')}</span>
                        <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />
                      </div>
                    </div>
                  </div>
                ) : reconcileResults ? (
                  <div className="space-y-6">
                    {/* Success Message */}
                    <div className={`p-4 rounded-lg flex items-start gap-3 ${
                      reconcileResults.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                    } ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                      {reconcileResults.success ? (
                        <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                      )}
                      <div className="flex-1">
                        <p className={`font-semibold ${
                          reconcileResults.success ? 'text-green-900' : 'text-red-900'
                        }`}>
                          {reconcileResults.message}
                        </p>
                      </div>
                    </div>

                    {/* Summary Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className={`bg-blue-50 rounded-lg p-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                        <p className="text-sm text-gray-600 mb-1">{t('totalAccounts')}</p>
                        <p className="text-2xl font-bold text-blue-600">{reconcileResults.totalAccounts}</p>
                      </div>
                      <div className={`bg-green-50 rounded-lg p-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                        <p className="text-sm text-gray-600 mb-1">{t('matched')}</p>
                        <p className="text-2xl font-bold text-green-600">{reconcileResults.reconciledAccounts}</p>
                      </div>
                      <div className={`bg-yellow-50 rounded-lg p-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                        <p className="text-sm text-gray-600 mb-1">{t('pending')}</p>
                        <p className="text-2xl font-bold text-yellow-600">{reconcileResults.pendingAccounts}</p>
                      </div>
                      <div className={`bg-purple-50 rounded-lg p-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                        <p className="text-sm text-gray-600 mb-1">{t('matchedItems')}</p>
                        <p className="text-2xl font-bold text-purple-600">{reconcileResults.matchedItems}</p>
                      </div>
                    </div>

                    {/* Details */}
                    <div>
                      <h3 className={`text-lg font-semibold text-gray-900 mb-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('reconciliationDetails')}</h3>
                      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                        <table className={`w-full text-sm ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                          <thead>
                            <tr className="bg-gray-50 border-b border-gray-200">
                              <th className={`py-3 px-4 font-semibold text-gray-700 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('account')}</th>
                              <th className={`py-3 px-4 font-semibold text-gray-700 text-center`}>{t('status')}</th>
                              <th className={`py-3 px-4 font-semibold text-gray-700 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('difference')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reconcileResults.details.map((detail, index) => (
                              <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                                <td className={`py-3 px-4 font-medium text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{detail.account}</td>
                                <td className="py-3 px-4 text-center">
                                  <span className={`px-2 py-1 text-xs font-medium rounded ${
                                    detail.status === 'Matched' 
                                      ? 'bg-green-100 text-green-700' 
                                      : 'bg-yellow-100 text-yellow-700'
                                  }`}>
                                    {detail.status === 'Matched' ? t('matched') : t('pending')}
                                  </span>
                                </td>
                                <td className={`py-3 px-4 font-semibold ${
                                  detail.difference === 0 ? 'text-green-600' : 'text-red-600'
                                } ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                                  SAR {detail.difference.toLocaleString()}
                                  {detail.outstandingItems > 0 && (
                                    <span className={`block text-xs text-gray-500 mt-1 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                      {detail.outstandingItems} {t('outstandingItemsFound')}
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Unmatched Items Warning */}
                    {reconcileResults.unmatchedItems > 0 && (
                      <div className={`bg-yellow-50 border border-yellow-200 rounded-lg p-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                        <div className={`flex items-start gap-3 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                          <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="font-semibold text-yellow-900 mb-1">{t('unmatchedItemsFound')}</p>
                            <p className="text-sm text-yellow-800">
                              {reconcileResults.unmatchedItems} {t('itemsRequireReview')}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>

              {/* Footer */}
              {!isReconciling && (
                <div className={`p-6 border-t border-gray-200 bg-gray-50 flex items-center gap-3 ${language === 'ar' ? 'flex-row-reverse justify-start' : 'justify-end'}`}>
                  <button
                    onClick={() => {
                      setShowReconcileModal(false);
                      setReconcileResults(null);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-100"
                  >
                    {t('close')}
                  </button>
                  {reconcileResults?.unmatchedItems > 0 && (
                    <button
                      onClick={() => {
                        setShowReconcileModal(false);
                        setReconcileResults(null);
                        alert(t('openingUnmatchedItems'));
                      }}
                      className="px-4 py-2 bg-yellow-600 text-white rounded-lg text-sm font-medium hover:bg-yellow-700"
                    >
                      {t('reviewUnmatchedItems')}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setShowReconcileModal(false);
                      setReconcileResults(null);
                      alert(t('exportingReport'));
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                  >
                    {t('exportReport')}
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ReconciliationTab;
